/**
 * Textarea <-> policy conversion for CIDR lists that carry display labels.
 *
 * A line may be annotated: `64.187.69.30/32  # Cheyenne office`. The label is
 * split off here and travels in a separate map, so it never reaches the CIDR the
 * server matches a login against — annotating an entry cannot change who gets in
 * or who is asked for an MFA code.
 */

/** Parse textarea content into the CIDRs to save plus their labels. */
export function parseCidrLines(text: string): {
  cidrs: string[]
  labels: Record<string, string>
} {
  const cidrs: string[] = []
  const labels: Record<string, string> = {}
  for (const raw of text.split('\n')) {
    const line = raw.trim()
    // A whole-line comment documents the list without adding an entry.
    if (!line || line.startsWith('#')) continue
    const hash = line.indexOf('#')
    if (hash === -1) {
      // No label, so commas may still separate several entries on one line.
      for (const part of line.split(',').map((p) => p.trim()).filter(Boolean)) {
        cidrs.push(part)
      }
      continue
    }
    const cidr = line.slice(0, hash).trim()
    if (!cidr) continue
    cidrs.push(cidr)
    const label = line.slice(hash + 1).trim()
    if (label) labels[cidr] = label.slice(0, 60)
  }
  return { cidrs, labels }
}

/** Render stored CIDRs back into annotated textarea lines. */
export function formatCidrLines(
  cidrs: string[],
  labels: Record<string, string> = {},
): string {
  return cidrs.map((c) => (labels[c] ? `${c}  # ${labels[c]}` : c)).join('\n')
}
